/**
 * TERRA Zustand Store — state management for the AI assistant panel
 */
import { create } from 'zustand';
import type { Conversation, Message, TerraAction, Citation, InlineComponent } from '@/api/terra';
import {
  createConversation,
  listConversations,
  loadConversation,
  deleteConversation as apiDeleteConversation,
  streamMessage,
} from '@/api/terra';
import { dispatchTerraAction, readDataVaultContext, navigateToPage } from '@/lib/terraBridge';
import { useDataVaultStore } from '@/store/dataVault';


// ── Tool call display state ─────────────────────────────────────────────

export interface ActiveToolCall {
  name: string;
  input: Record<string, unknown>;
  isActive: boolean;
  summary?: string;
  durationMs?: number;
}

// ── Follow-up suggestion derivation ─────────────────────────────────────

const FOLLOW_UP_MAP: Record<string, string[]> = {
  query_chemistry: ['Show me the trend over time', 'Which wells exceed regulatory limits?'],
  get_exceedances: ['What remediation is recommended?', 'Show exceedances on a map'],
  get_predictions: ['How confident are these predictions?', 'Predicted timeline to closure?'],
  get_site_summary: ['Show me the chemistry data', 'What are the key risks?'],
  get_trend_analysis: ['What is driving this trend?', 'Compare to other sites'],
  check_regulatory_limits: ['Which wells are closest to limits?', 'Show compliance matrix'],
  query_gauging: ['Show water level trends', 'Compare to historical levels'],
  search_documents: ['Summarize the key findings', 'What actions are recommended?'],
  explain_concept: ['How does this apply to my site?', 'What are the regulatory requirements?'],
  generate_report_section: ['Generate the next section', 'Review and refine this section'],
};

function deriveFollowUps(toolCalls: ActiveToolCall[]): string[] {
  // Only show follow-ups when tools were actually used
  if (toolCalls.length === 0) return [];

  const seen = new Set<string>();
  const suggestions: string[] = [];
  for (const tc of toolCalls) {
    const options = FOLLOW_UP_MAP[tc.name];
    if (options) {
      for (const s of options) {
        if (!seen.has(s)) {
          seen.add(s);
          suggestions.push(s);
        }
      }
    }
  }
  if (suggestions.length === 0) {
    return ['Tell me more', 'Can you show this as a chart?'];
  }
  return suggestions.slice(0, 3);
}

// ── Store Interface ─────────────────────────────────────────────────────

interface TerraStore {
  // Panel state
  isOpen: boolean;
  isPinned: boolean;

  // Conversations
  conversations: Conversation[];
  activeConversationId: string | null;

  // Messages for active conversation
  messages: Message[];

  // Streaming state
  isStreaming: boolean;
  streamingText: string;
  activeToolCalls: ActiveToolCall[];

  // Follow-up suggestions (derived from tool calls after response)
  followUpSuggestions: string[];

  // Inline citations from tool calls
  citations: Citation[];

  // Pending inline components (charts/tables) from tool results
  pendingComponents: InlineComponent[];

  // Reasoning/thinking trace
  thinkingText: string;

  // Rate limit countdown (timestamp when limit expires)
  rateLimitUntil: number | null;

  // UI highlights (delegated to dataVault store)
  highlightedWells: string[];

  // Actions
  toggle: () => void;
  open: () => void;
  close: () => void;
  pin: () => void;
  unpin: () => void;

  // Conversation management
  fetchConversations: (siteId?: string) => Promise<void>;
  newConversation: (siteId?: string) => Promise<string>;
  switchConversation: (id: string) => Promise<void>;
  removeConversation: (id: string) => Promise<void>;

  // Messaging
  sendMessage: (content: string) => void;
  cancelStream: () => void;

  // Highlights
  setHighlightedWells: (wells: string[]) => void;
  clearHighlights: () => void;
}

// Internal ref for abort controller
let _abortController: AbortController | null = null;

export const useTerraStore = create<TerraStore>((set, get) => ({
  // Initial state
  isOpen: false,
  isPinned: false,
  conversations: [],
  activeConversationId: null,
  messages: [],
  isStreaming: false,
  streamingText: '',
  activeToolCalls: [],
  followUpSuggestions: [],
  citations: [],
  pendingComponents: [],
  thinkingText: '',
  rateLimitUntil: null,
  highlightedWells: [],

  // Panel
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),
  open: () => set({ isOpen: true }),
  close: () => {
    if (!get().isPinned) set({ isOpen: false });
  },
  pin: () => set({ isPinned: true }),
  unpin: () => set({ isPinned: false }),

  // Conversations
  fetchConversations: async (siteId?: string) => {
    try {
      const convos = await listConversations(siteId);
      set({ conversations: convos });
    } catch (err) {
      console.error('TERRA: Failed to fetch conversations', err);
    }
  },

  newConversation: async (siteId?: string) => {
    try {
      const convo = await createConversation(siteId);
      set((s) => ({
        conversations: [convo, ...s.conversations],
        activeConversationId: convo.id,
        messages: [],
        streamingText: '',
        activeToolCalls: [],
      }));
      return convo.id;
    } catch (err) {
      console.error('TERRA: Failed to create conversation', err);
      throw err;
    }
  },

  switchConversation: async (id: string) => {
    try {
      const detail = await loadConversation(id);
      set({
        activeConversationId: id,
        messages: detail.messages,
        streamingText: '',
        activeToolCalls: [],
      });
    } catch (err) {
      console.error('TERRA: Failed to load conversation', err);
    }
  },

  removeConversation: async (id: string) => {
    try {
      await apiDeleteConversation(id);
      set((s) => {
        const convos = s.conversations.filter((c) => c.id !== id);
        const newActive = s.activeConversationId === id
          ? (convos[0]?.id || null)
          : s.activeConversationId;
        return {
          conversations: convos,
          activeConversationId: newActive,
          messages: s.activeConversationId === id ? [] : s.messages,
        };
      });
    } catch (err) {
      console.error('TERRA: Failed to delete conversation', err);
    }
  },

  // Messaging
  sendMessage: (content: string) => {
    const { activeConversationId, isStreaming } = get();
    if (!activeConversationId || isStreaming) return;

    // Add user message to local state immediately
    const userMsg: Message = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      tool_name: null,
      tool_input: null,
      tool_output: null,
      ui_actions: null,
      tokens_used: 0,
      latency_ms: null,
      rating: null,
      feedback_text: null,
      created_at: new Date().toISOString(),
    };

    set((s) => ({
      messages: [...s.messages, userMsg],
      isStreaming: true,
      streamingText: '',
      thinkingText: '',
      activeToolCalls: [],
      followUpSuggestions: [],
      citations: [],
      pendingComponents: [],
    }));

    // Read Data Vault context for the request
    const context = readDataVaultContext();

    // Batch delta updates to avoid overwhelming React with renders.
    // Accumulate text and flush to state at most every 50ms.
    let _deltaBuffer = '';
    let _deltaTimer: ReturnType<typeof setTimeout> | null = null;
    const flushDelta = () => {
      if (_deltaBuffer) {
        const text = _deltaBuffer;
        _deltaBuffer = '';
        set((s) => ({ streamingText: s.streamingText + text }));
      }
      _deltaTimer = null;
    };

    _abortController = streamMessage(activeConversationId, content, context, {
      onDelta: ({ text }) => {
        _deltaBuffer += text;
        if (!_deltaTimer) {
          _deltaTimer = setTimeout(flushDelta, 50);
        }
      },

      onToolStart: ({ tool_name, tool_input }) => {
        // Flush any pending delta buffer before tool call
        flushDelta();
        set((s) => {
          // If there's accumulated streaming text before a tool call, save it
          // as an assistant message so it doesn't get lost
          const newMessages = [...s.messages];
          if (s.streamingText) {
            newMessages.push({
              id: `temp-assistant-${Date.now()}`,
              role: 'assistant',
              content: s.streamingText,
              tool_name: null,
              tool_input: null,
              tool_output: null,
              ui_actions: null,
              tokens_used: 0,
              latency_ms: null,
              rating: null,
              feedback_text: null,
              created_at: new Date().toISOString(),
            });
          }
          return {
            messages: newMessages,
            streamingText: '',
            activeToolCalls: [
              ...s.activeToolCalls,
              { name: tool_name, input: tool_input, isActive: true },
            ],
          };
        });
      },

      onToolEnd: ({ tool_name, summary, duration_ms }) => {
        set((s) => {
          // Mark the FIRST active tool call with this name as complete
          let matched = false;
          const updated = s.activeToolCalls.map((tc) => {
            if (tc.name === tool_name && tc.isActive && !matched) {
              matched = true;
              return { ...tc, isActive: false, summary, durationMs: duration_ms };
            }
            return tc;
          });
          return { activeToolCalls: updated };
        });
      },

      onAction: (action: TerraAction) => {
        // Page navigation
        if (action.type === 'navigate_page' && 'path' in action) {
          navigateToPage(action.path);
          return;
        }

        // Dispatch to Data Vault bridge
        dispatchTerraAction(action);

        if (action.type === 'highlight' && 'wellNames' in action) {
          useDataVaultStore.getState().setHighlightedWells(action.wellNames);
          set({ highlightedWells: action.wellNames });
        }
      },

      onThinking: ({ text }) => {
        set((s) => ({ thinkingText: s.thinkingText + text }));
      },

      onUIComponent: (component: InlineComponent) => {
        set((s) => ({ pendingComponents: [...s.pendingComponents, component] }));
      },

      onDone: (data) => {
        // Flush pending delta buffer directly into state and finalize
        if (_deltaTimer) {
          clearTimeout(_deltaTimer);
          _deltaTimer = null;
        }

        set((s) => {
          const finalText = s.streamingText + _deltaBuffer;
          _deltaBuffer = '';

          // Persist completed tool calls as messages so chips survive after streaming
          const toolMessages = s.activeToolCalls
            .filter((tc) => !tc.isActive)
            .map((tc) => ({
              id: `temp-tool-${tc.name}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              role: 'tool_call' as const,
              content: null,
              tool_name: tc.name,
              tool_input: tc.input,
              tool_output: null,
              ui_actions: null,
              tokens_used: 0,
              latency_ms: tc.durationMs ?? null,
              rating: null,
              feedback_text: null,
              created_at: new Date().toISOString(),
              _summary: tc.summary,
            }));

          const newMessages = [...s.messages, ...toolMessages];

          if (finalText) {
            newMessages.push({
              id: `temp-assistant-${Date.now()}`,
              role: 'assistant' as const,
              content: finalText,
              tool_name: null,
              tool_input: null,
              tool_output: null,
              ui_actions: null,
              tokens_used: 0,
              latency_ms: null,
              rating: null,
              feedback_text: null,
              created_at: new Date().toISOString(),
              ...(s.pendingComponents.length ? { _components: [...s.pendingComponents] } : {}),
            } as any);
          }

          // Use LLM-generated suggestions if available, fallback to static derivation
          const suggestions = data?.suggestions?.length
            ? data.suggestions
            : deriveFollowUps(s.activeToolCalls);

          return {
            messages: newMessages,
            isStreaming: false,
            streamingText: '',
            thinkingText: '',
            activeToolCalls: [],
            pendingComponents: [],
            followUpSuggestions: suggestions,
            citations: data?.citations || [],
          };
        });

        _abortController = null;
      },

      onError: (errData: any) => {
        const { code, message } = errData;
        // Clear any pending delta timer
        if (_deltaTimer) { clearTimeout(_deltaTimer); _deltaTimer = null; }
        _deltaBuffer = '';

        // Handle rate limiting with countdown
        const retryAfter = errData.retry_after_seconds;
        if (code === 'rate_limited' && retryAfter) {
          set({ rateLimitUntil: Date.now() + retryAfter * 1000 });
        }

        // Show error as assistant message
        const errorMsg: Message = {
          id: `temp-error-${Date.now()}`,
          role: 'assistant',
          content: `**Error:** ${message}`,
          tool_name: null,
          tool_input: null,
          tool_output: null,
          ui_actions: null,
          tokens_used: 0,
          latency_ms: null,
          rating: null,
          feedback_text: null,
          created_at: new Date().toISOString(),
        };

        set((s) => ({
          messages: [...s.messages, errorMsg],
          isStreaming: false,
          streamingText: '',
        }));

        _abortController = null;
      },
    });
  },

  cancelStream: () => {
    if (_abortController) {
      _abortController.abort();
      _abortController = null;
    }
    set({ isStreaming: false, streamingText: '' });
  },

  // Highlights — delegate to centralized dataVault store
  setHighlightedWells: (wells) => {
    useDataVaultStore.getState().setHighlightedWells(wells);
    set({ highlightedWells: wells });
  },
  clearHighlights: () => {
    useDataVaultStore.getState().clearHighlights();
    set({ highlightedWells: [] });
  },
}));
